import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {AudioWaveform} from '@Components/audio-waveform';
import {TimelineItem} from '../timeline-item/timeline-item';
import {TrimGhost} from '../trim-ghost/trim-ghost';
import {ITEM_TRACK_HEIGHT, MIN_ITEM_DURATION, TimelineItemType} from '../../timeline.types';
import type {DragModifyingData, ResizeModifyingData, ResizeCalculationParams, ResizeCalculationResult} from '../timeline-item/timeline-item.types';
import styles from './audio-timeline-item.module.scss';
import {useAppSelector} from '@/hooks';
import {getAudioItemFromCurrentPageById} from '@Components/poster-editor/poster-editor-reducer';
import {useTimelineWidthPerSecond, useIsItemDragging, useItemModifying} from '@Components/poster-editor/components/timeline/hooks/timeline-hooks';

interface AudioTimelineItemProps {
  id: string;
  startTime: number;
  endTime: number;
  subRow: number;
  onClick?: (id: string) => void;
  onDragEnd?: (id: string, data: DragModifyingData) => Promise<void>;
  onResizeEnd?: (id: string, data: ResizeModifyingData) => Promise<void>;
}

export function AudioTimelineItem({id, startTime, endTime, subRow, onClick, onDragEnd, onResizeEnd}: AudioTimelineItemProps): ReactElement | null {
  const audioItemData = useAppSelector((state) => getAudioItemFromCurrentPageById(state, id));
  const selectedAudioItemUID = useAppSelector((state) => {
    const currentPageId = state.posterEditor.currentPageId;
    return state.posterEditor.posterObject?.pages?.pagesHashMap?.[currentPageId]?.audioClips?.selectedAudioItemUID;
  });
  const isHovered = useAppSelector((state) => state.timeline.hoveredItemId === id);
  const isDragging = useIsItemDragging(id);
  const itemModifying = useItemModifying();
  const isResizing = itemModifying?.itemId === id && itemModifying?.action.type === 'resizing';
  const widthPerSecond = useTimelineWidthPerSecond();

  /**
   * Custom resize calculation for audio items.
   * - Left handle: adjusts trim.startTime, constrained to >= 0
   * - Right handle: adjusts trim.endTime, constrained to <= originalDuration
   */
  const handleCalculateResize = (params: ResizeCalculationParams): ResizeCalculationResult => {
    if (!audioItemData) {
      return {startTime: params.originalStartTime, endTime: params.originalEndTime};
    }

    const {originalStartTime, originalEndTime, deltaPixels, handle, pixelsPerSecond} = params;
    const {speed, originalDuration, trim} = audioItemData.audioPlayer;
    const deltaTime = deltaPixels / pixelsPerSecond;

    if (handle === 'left') {
      const trimDelta = deltaTime * speed;
      const newTrimStart = trim.startTime + trimDelta;

      // Constrain: trim.startTime cannot go below 0
      if (newTrimStart < 0) {
        const maxDelta = -trim.startTime / speed;
        const newStart = originalStartTime + maxDelta;
        return {startTime: Math.min(newStart, originalEndTime - MIN_ITEM_DURATION), endTime: originalEndTime};
      }

      const newStart = originalStartTime + deltaTime;
      if (originalEndTime - newStart < MIN_ITEM_DURATION) {
        return {startTime: originalEndTime - MIN_ITEM_DURATION, endTime: originalEndTime};
      }

      return {startTime: newStart, endTime: originalEndTime};
    } else {
      const trimDelta = deltaTime * speed;
      const newTrimEnd = trim.endTime + trimDelta;

      // Constrain: trim.endTime cannot exceed originalDuration
      if (newTrimEnd > originalDuration) {
        const maxDelta = (originalDuration - trim.endTime) / speed;
        const newEnd = originalEndTime + maxDelta;
        return {startTime: originalStartTime, endTime: Math.max(newEnd, originalStartTime + MIN_ITEM_DURATION)};
      }

      const newEnd = originalEndTime + deltaTime;
      if (newEnd - originalStartTime < MIN_ITEM_DURATION) {
        return {startTime: originalStartTime, endTime: originalStartTime + MIN_ITEM_DURATION};
      }

      return {startTime: originalStartTime, endTime: newEnd};
    }
  };

  if (audioItemData === undefined) {
    return null;
  }

  const isSelected = id === selectedAudioItemUID;
  const name = audioItemData?.name ?? window.i18next.t('pmwjs_audio');
  const showTrimGhost = isSelected && !isDragging;

  const {fadeInDuration, fadeOutDuration} = audioItemData.audioPlayer.fade;
  const fadeInWidth = fadeInDuration * widthPerSecond;
  const fadeOutWidth = fadeOutDuration * widthPerSecond;

  const getLeftForAudioWaveform = (): string | undefined => {
    if (!audioItemData) {
      return undefined;
    }
    const existingLeft = -audioItemData.audioPlayer.trim.startTime * widthPerSecond;

    if (isResizing && itemModifying?.action.type === 'resizing' && itemModifying.action.resizeHandle === 'left') {
      const differenceInStartTimeOnPoster = itemModifying.startTime - startTime;
      return `${existingLeft + -differenceInStartTimeOnPoster * widthPerSecond}px`;
    }

    return `${existingLeft}px`;
  };

  return (
    <>
      <TrimGhost id={id} audioItemData={audioItemData} startTime={startTime} subRow={subRow} widthPerSecond={widthPerSecond} isVisible={showTrimGhost} />
      <TimelineItem
        id={id}
        startTime={startTime}
        endTime={endTime}
        subRow={subRow}
        variant={TimelineItemType.AUDIO}
        label={name}
        trackHeight={ITEM_TRACK_HEIGHT}
        isSelected={isSelected}
        onClick={onClick}
        onDragEnd={onDragEnd}
        onResizeEnd={onResizeEnd}
        onCalculateResize={handleCalculateResize}
      >
        <div className={styles.waveformWrapper}>
          <AudioWaveform
            style={{
              background: 'transparent',
              top: 0,
              left: getLeftForAudioWaveform(),
              width: `${(audioItemData.audioPlayer.originalDuration * widthPerSecond) / audioItemData.audioPlayer.speed}px`,
            }}
            doShowLoader={false}
            additionalClasses={`${styles.waveform} ${isDragging ? styles.isDragging : ''}`}
            waveSurferProps={{
              backgroundColor: 'transparent',
              waveColor: '#CABBF3',
              barWidth: undefined,
              barRadius: undefined,
              barMinHeight: undefined,
              barGap: undefined,
              height: ITEM_TRACK_HEIGHT - 8,
              audioRate: audioItemData.audioPlayer.speed,
              cursorWidth: 0,
            }}
            id={`audiowaveform-${id}`}
            key={`audiowaveform-${id}-${audioItemData.audioPlayer.speed}`}
            url={audioItemData.audioPlayer.audioUrl}
            isPlaying={false}
            endTime={audioItemData.audioPlayer.originalDuration}
            seek={0}
            duration={audioItemData.audioPlayer.originalDuration}
            pxPerSec={widthPerSecond}
          />
        </div>
        {fadeInWidth > 0 ? (
          <div className={styles.fadeOverlay} style={{left: 0, width: fadeInWidth, background: 'linear-gradient(to right, #2C2C38E5, transparent)'}} />
        ) : null}
        {fadeOutWidth > 0 ? (
          <div className={styles.fadeOverlay} style={{right: 0, width: fadeOutWidth, background: 'linear-gradient(to left, #2C2C38E5, transparent)'}} />
        ) : null}
        <div className={`${isHovered || isSelected || isDragging ? '' : '_hidden'} ${styles.content}`}>
          <div className={styles.iconWrapper}>
            <Icon icon="icon-audio" size={IconSize.SIZE_ICON_12} className={styles.icon} />
          </div>
          <span className={styles.name}>{name}</span>
        </div>
      </TimelineItem>
    </>
  );
}
